import type { Compare } from "@suse-o11y/compare";
import { getTime } from "@suse-o11y/date-time";
import { identity } from "@suse-o11y/identity";
import { isStrictEqual } from "@suse-o11y/is-strict-equal";
import { max } from "@suse-o11y/max";
import { type Monitor, Signal, useComputed } from "@suse-o11y/signal";
import { useInterval } from "@suse-o11y/use-interval";
import { usePermanent } from "@suse-o11y/use-permanent";
import { useLayoutEffect, useRef } from "react";

import { MINIMAL_FLOOR_INTERVAL, type UseNowFloor, floorMilliseconds } from "./floor";

export interface UseNowOptions {
	/**
	 * An interval in milliseconds for updating the current time.
	 *
	 * @default * The default (and minimal) value differs per floor:
	 * - `milliseconds`: ~16ms (60 FPS)
	 * - `seconds`: ~100ms (10 FPS)
	 * - `minutes`: ~1000ms (1 FPS)
	 *
	 * 10 FPS means that setInterval will call the callback 10 times per second,
	 * so when the `floor` is `seconds`, the `now` value will have 100ms margin with the real time.
	 */
	interval?: number;

	/**
	 * When paused, the setInterval does not run, so nothing forces the hook to trigger re-render.
	 * In this case the `now` value updates only when the hook is re-rendered by other means.
	 * That means that the `now` value might be outdated.
	 * It is useful, when the `transform` function returns a value that does not depend on the current time.
	 *
	 * @default false
	 */
	pause?: boolean;

	/**
	 * The floor to which the current time is rounded.
	 * For example, when the floor is `seconds`, the `now` value will be rounded to the nearest second.
	 *
	 * @default "seconds"
	 *
	 * @example
	 * // milliseconds
	 * 12345 -> 12345
	 * // seconds
	 * 12345 -> 12000
	 * // hours
	 * 62345 -> 60000
	 */
	floor?: UseNowFloor;

	/**
	 * The compare function used to compare the previous and the current transformed values.
	 * If the values are equal, the hook does not trigger re-render.
	 * It means, that even if interval is 16ms, but the `transform` returns rounded seconds,
	 * the hook will not trigger re-render every 16ms, but only every second (setInterval will tick each 16ms tho).
	 *
	 * @default isStrictEqual
	 */
	compare?: Compare<number>;
}

export interface UseNowTransformOptions<TResult> extends Omit<UseNowOptions, "compare"> {
	compare?: Compare<TResult>;

	/**
	 * The function that converts the current time in milliseconds to the desired value.
	 */
	transform: (now: number, monitor: Monitor) => TResult;
}

export function useNow<TResult>(options: UseNowTransformOptions<TResult>): TResult;
export function useNow(options?: UseNowOptions): number;
export function useNow<TResult>({
	interval = 1,
	pause = false,
	floor = "seconds",
	compare = isStrictEqual,
	transform = identity as (now: number) => TResult,
}: Partial<UseNowTransformOptions<TResult>> = {}): TResult {
	const time = usePermanent(() => Signal(floorMilliseconds(Date.now(), floor)));
	const floorRef = useRef(floor);

	const adjustedInterval = max(MINIMAL_FLOOR_INTERVAL[floor], interval);
	const isDisabled = pause || !isFinite(adjustedInterval);

	useLayoutEffect(() => {
		floorRef.current = floor;
	}, [floor]);

	useInterval(
		(now) => {
			time.write(floorMilliseconds(getTime(now), floorRef.current));
		},
		[time],
		adjustedInterval,
		{
			leading: true,
			pause: isDisabled,
		},
	);

	return useComputed(
		(monitor) => {
			const now = isDisabled ? floorMilliseconds(Date.now(), floor) : time.read(monitor);

			return transform(now, monitor);
		},
		[time, isDisabled, floor, transform],
		{ equals: compare },
	);
}

export type { UseNowFloor } from "./floor";
